import { denialReason, type Ability } from '@/lib/permissions';

/*
 * How the two newest catalogue masters gate: the ADMIN TIER **and**
 * `reference-data`, which are two different guards and are both real.
 *
 * `/billing-region-defaults` and `/contact-groups` both carry the reference-data
 * guard profile — `authenticate` on the reads, `requireRole(...ADMIN_ROLES)` on
 * every write. **That role guard is the server's, it is unchanged, and it is
 * what actually refuses a request**, so it stays and is checked first.
 *
 * 🔴 **THE SECOND HALF IS A REVERSAL — owner's decision, 2026-09-02.**
 * *SUPERSEDED: "This is deliberately NOT `denialReason(ability, 'update',
 * 'reference-data')`. Borrowing a neighbouring slug happens to give the right
 * answer today and is a claim about a module these two endpoints are not in;
 * the day `reference-data` gains real enforcement, this screen would start
 * reporting a denial that has nothing to do with it."*
 *
 * That was sound while these two were strays. The owner has ruled that
 * `reference-data` governs the WHOLE catalogue — one tickbox for the screen a
 * person thinks of as one screen — so these endpoints are IN that module by
 * decision rather than outside it by omission. It is no longer a neighbouring
 * slug; it is theirs. `TargetPeriodsCard` already gated this way, so the three
 * cards on `/catalog` now answer one question instead of two.
 *
 * ⚠️ **The over-reporting objection was real and is answered by the ORDER, not
 * by the reversal.** The seeded `advanced-user` set claims reference-data writes
 * the routers refuse, so a permission-only gate would render an enabled control
 * that collects a 403. Testing `isAdmin` first makes that unreachable — and
 * `enforcedByRole` independently answers `admin` for `reference-data` writes, so
 * the permission list cannot widen past the tier even if this function forgot.
 *
 * ⚠️ **The permission half is INERT TODAY and must not be described as working.**
 * `can()` short-circuits the admin tier, so an admin always holds
 * `reference-data.update` and nobody below admin can reach these writes at all:
 * unticking the box on a set changes nothing on this screen. It is written
 * because it is the true expression of the rule and because the three cards must
 * not disagree, not because it currently decides anything.
 */

/**
 * Whether this account may write the admin-tier catalogue masters.
 *
 * ⚠️ **Both halves, and the tier first.** See above — the role is the server's
 * guard and the permission is a narrower client filter on top of it. Never the
 * other way round: a permission set cannot admit somebody `requireRole` refuses.
 */
export function canWriteAdminMaster(ability: Ability, module: ReferenceModule): boolean {
  /*
   * 2026-10-01: the set decides for every role (owner's decision); the backend
   * gates these masters on `reference-data`, not on the admin tier. *SUPERSEDED:
   * `ability.isAdmin && ability.canUpdate('reference-data')`, and the "tier
   * first" reasoning above.*
   * 2026-10-02: the catalogue is split into `reference-*` modules, so each card
   * names its own. *SUPERSEDED: `reference-data` for every card.*
   */
  return ability.canUpdate(module);
}

/** The catalogue modules a card may name — owner's split, 2026-10-02. */
export type ReferenceModule =
  | 'reference-data'
  | 'reference-geography'
  | 'reference-sales'
  | 'reference-planning'
  | 'reference-people';

/**
 * The reason this account cannot write these masters, or `null` when it can.
 *
 * ⚠️ **The branches are ORDERED, and the order is `denialReason`'s own** — which
 * is now delegated to rather than re-derived, so there is one copy of the
 * wording in the app instead of two that agree until somebody edits one. A
 * `viewer` is not denied for wanting administrator rights (reading is the only
 * thing their account does), so read-only is tested before the administrator
 * sentence; that ordering is the whole content of the helper.
 *
 * ⚠️ **The `??` is NOT a silent drop.** `denialReason` returns `null` exactly
 * when the TIER allows the write, so the two sides cannot both be true: reaching
 * the right-hand side means an administrator was denied by the permission set,
 * and "restricted to administrators" would name a thing they already are.
 *
 * ONE copy, shared by both cards, so the same refusal cannot be worded two ways
 * on one screen.
 */
export function adminWriteDenial(ability: Ability, module: ReferenceModule): string | null {
  if (canWriteAdminMaster(ability, module)) return null;
  return (
    denialReason(ability, 'update', module) ??
    'Your permission set does not include this. An administrator can change that.'
  );
}
